import React, { useState, useEffect, useRef } from 'react';
import io from 'socket.io-client';
import {
  Row,
  Col,
  Avatar,
  Button,
  Input,
  Divider,
  Typography,
  Card,
  Modal,
  Drawer,
  Tag,
  Spin,
} from 'antd';
import moment from 'moment';
import { CloseOutlined, SendOutlined } from '@ant-design/icons';
import EnjoAction from '../icons/enjoAvatar.svg';
import axios from 'axios';
import { v4 as uuidv4 } from 'uuid';
import Like from '../icons/like.svg';
import UnLike from '../icons/dislike.svg';
import HighlightedLike from '../icons/highlighted-like.svg';
import HighlightedUnLike from '../icons/highlighted-dislike.svg';
import BottomLinksBar from './bottom-links-bar';
import { useIsMobile } from '../hooks/screen-size';
import BottomNavBar from './bottom-nav-bar';
import TopNavBar from './top-nav-bar';
import { useLocation } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { updateChats, updateCurrentBot } from '../store/conversations-slice';
import { getCenterSuggestions, TICKET_CANCEL_TEXT } from '../utils/utils';
import TicketCreateForm from '../components/TicketForm';
import { getTicketingFields } from '../services/api-call';
import { debounce } from 'lodash';

const { Title, Text } = Typography;

const Showcase = ({ questionText, setQuestionText }) => {
  const [conversation, setConversation] = useState([]);
  const isMobile = useIsMobile();
  const location = useLocation();
  const dispatch = useDispatch();
  const chats = useSelector((state) => state.conversations.chats);
  const { selectedBot } = useSelector((state) => state.bots);
  const [loading, setLoading] = useState(false);
  const [showSynthesis, setShowSynthesis] = useState(false);
  const [synthesisData, setSynthesisData] = useState(false);
  const [conversationId, setConversationId] = useState('');
  const [actionTriggerMessage, setActionTriggerMessage] = useState('');
  const [feedbackModal, setFeedbackModal] = useState(false);
  const [signInModal, setSignInModal] = useState(false);
  const [clearButton, setClearButton] = useState(false);
  const [ticketForm, setTicketForm] = useState({
    isOpen: false,
    isLoading: false,
    data: null,
  });

  const [feedbackData, setFeedbackData] = useState({
    isPositive: false,
    comment: '',
  });

  const messageEl = useRef(null);
  const messageEl2 = useRef(null);
  const [socket, setSocket] = useState(null);

  useEffect(() => {
    const fetchToken = async () => {
      try {
        setLoading(true);
        let token = localStorage.getItem('token');
        if (!token === true) {
          const response = await axios.get('/api/auth.showcase.token');
          token = response.data.token;
          localStorage.setItem('token', token);
        }

        const socketIo = io(process.env.REACT_APP_SOCKET_DOMAIN, {
          path: '/wsproxy', // Custom path for WebSocket
          query: { jwtToken: token },
        });
        setSocket(socketIo);

        socketIo.on('answer', handleAnswer);
        socketIo.on('actionCancelled', handleActionCancelled);
        socketIo.on('progress', handleProgress);
        socketIo.on('error', handleError);
        socketIo.on('feedbackSubmit', handleFeedbackSubmit);
        socketIo.on('messageLimitExceeded', handleLimitExceeded);
        socketIo.on('ticket_creation', handleNewTicket);
        socketIo.on('ticket_error', handleTicketError);
        socketIo.on('ticket_response', handleTicketCreateFromMessage )
        setLoading(false);
        const currentBotChats = chats.find(
          (chat) => chat?.botId === selectedBot?.botId
        );
        dispatch(updateCurrentBot(selectedBot?.botId));
        if (currentBotChats?.chats) {
          setConversation([...currentBotChats.chats]);
        }
        return () => {
          socketIo.off('answer', handleAnswer);
          socketIo.off('actionCancelled', handleActionCancelled);
          socketIo.off('progress', handleProgress);
          socketIo.off('feedbackSubmit', handleFeedbackSubmit);
          socketIo.on('ticket_response', handleTicketCreateFromMessage )
        };
      } catch (error) {
        console.error('Error fetching token:', error);
        setLoading(false);
      }
    };
    const data = location.state;
    setQuestionText(data?.question);
    fetchToken();
  }, []);

  const handleActionCancelled = (message) => {
    setConversation((prevConversation) => [
      ...prevConversation.slice(0, -1),
      {
        type: 'answer',
        text: message?.text,
        messageAt: Date.now(),
        messageId: uuidv4(),
      },
    ]);
    dispatch(updateChats([...conversation]));
  };

  const handleTicketCreateFromMessage = (message) => {
    const temp = []
    setConversation((prev) => {
      temp.push(...prev)
      return [...prev];
    });
    if (temp?.length > 1) {
      handleCreateTicket(
        temp[temp?.length - 2],
        message?.shouldCreateTicket ? 'yes' : 'no',
        false,
        message?.botId
      );
    }
  };

  useEffect(() => {
    dispatch(updateCurrentBot(selectedBot?.botId));
    const currentBotChats = chats.find(
      (chat) => chat?.botId === selectedBot?.botId
    );
    if (currentBotChats?.chats?.length > 0) {
      setConversation([...currentBotChats.chats]);
    } else {
      setConversation([]);
    }
  }, [selectedBot?.botId]);

  const handleProgress = (message) => {
    setConversation((prevConversation) => {
      const messageId = message?.messageId;
      const newMessage = {
        messageId,
        type: 'progressing',
        text: message?.text,
        messageAt: Date.now(),
      };

      const updatedConversation = prevConversation.map((msg) =>
        msg?.messageId === messageId ? newMessage : msg
      );

      const isMessageExists = prevConversation.some(
        (msg) => msg?.messageId === messageId
      );

      dispatch(
        updateChats(
          isMessageExists
            ? updatedConversation
            : [...prevConversation, newMessage]
        )
      );
      return isMessageExists
        ? updatedConversation
        : [...prevConversation, newMessage];
    });
  };
  
  const handleError = (message) => {
    console.error(' handleError ~ message:', message)
    setConversation((prevConversation) => {
      const messageId = message?.messageId;
      const newMessage = {
        messageId,
        type: 'feedback',
        text: 'I am unable to answer at the moment',
        messageAt: Date.now(),
      };
      dispatch(updateChats([...prevConversation, newMessage]));
      return [...prevConversation, newMessage];
    });
  };

  const handleTicketError =(message) =>{
    updateConversation({
      ...message,
      type: 'answer',
      hideFeedback: true,
      text: 'There was a problem creating the ticket. Please let me know if I can provide any further information.',
    });
  }

  const handleFeedbackSubmit = (message) => {
    const { conversationId, feedbackSubmitted } = message;
    if (feedbackSubmitted) {
      updateConversation({
        type: 'feedback',
        text: '✅ Thank you for submitting your feedback.',
        messageId: false,
        conversationId,
      });
    }
  };
  const sendMessage = (socketText, message) => {
    if (socket) {
      socket.emit(socketText, { ...message, botId: selectedBot?.botId });
    } else {
      console.error('Socket is not initialized.');
    }
  };

  const handleAnswer = (message) => {
    const {
      hasAnswer,
      answers,
      actionExists,
      response,
      conversationId,
      isActionDone,
      messageId,
      type,
      requestType,
      canCreateTicket,
    } = message;
    if (type === 'ask_action_confirmation') {
      setClearButton(true);
    }
    setConversationId(conversationId);
    if (hasAnswer) {
      updateConversation({
        type: type || 'answer',
        text: answers[0]?.answer,
        messageId,
        conversationId,
        data: message,
      });
    } else if (actionExists) {
      setActionTriggerMessage(message?.originalMessage);
      updateConversation({
        type: type === 'ask_action_confirmation' ? type : 'answer',
        text: response || 'Are you sure you want to perform the action?',
        messageId,
        conversationId,
        data: message,
      });
    } else if (isActionDone) {
      setConversationId('');
      setActionTriggerMessage('');
      updateConversation({
        type: type || 'answer',
        text: response || answers || 'Action Done',
        messageId,
        conversationId,
      });
    } else if (type === 'action') {
      updateConversation({
        type,
        text: answers || message?.confirmationText,
        messageId,
        conversationId,
      });
    } else if (type === 'ticket_suggestion') {
      updateConversation({
        type,
        text: response,
        messageId,
        conversationId,
        newMessageId: uuidv4(),
        data: message,
        ticketInfo: message?.ticketInfo,
      });
    } else {
      updateConversation({
        type: canCreateTicket ? 'no_answer' : 'action',
        text: canCreateTicket
          ? requestType
            ? `Sorry! I don’t have an answer for this. Do you want to create a <b>${requestType} request </b>?`
            : 'Sorry! I don’t have an answer for this. Do you want to create a ticket?'
          : 'Sorry! I don’t have an answer for this.',

        messageId,
        conversationId,
        newMessageId: uuidv4(),
        data: message,
      });
    }
  };

  const handleNewTicket = (message) => {
    const { conversationId, text, ticketData, messageId } = message;
    updateConversation({
      type: 'ticket',
      text,
      conversationId,
      data: ticketData,
      messageId,
    });
  };
  const updateConversation = ({
    type,
    text,
    messageId,
    conversationId,
    messageAt,
    data,
  }) => {
    setConversation((prevConversation) => {
      const newMessage = {
        messageId,
        type,
        text,
        messageAt: messageAt ?? Date.now(),
        conversationId,
      };
      if (data) {
        newMessage.data = data;
      }

      const index = prevConversation.findIndex(
        (msg) => msg?.messageId === messageId && messageId
      );

      if (index !== -1) {
        const updatedConversation = [...prevConversation];
        updatedConversation[index] = newMessage;
        dispatch(updateChats(updatedConversation));
        return updatedConversation;
      } else {
        dispatch(updateChats([...prevConversation, newMessage]));
        return [...prevConversation, newMessage];
      }
    });
  };

  const deleteConversationWithMessageId = (messageId) => {
    setConversation((prevConversation) => {
      const updatedConversation = prevConversation.filter(
        (el) => el?.messageId !== messageId
      );
      dispatch(updateChats(updatedConversation));
      return updatedConversation;
    });
  };

  const handleSendMessage = () => {
    if (questionText.trim()) {
      let temp = [...conversation];
      const lastMessage = temp[temp?.length -1]
      if (lastMessage?.type === 'ticket_suggestion') {
      updateConversation({
          ...lastMessage,
          type: 'action',
          data: {
            ...lastMessage?.data,
            hideFeedback: true,
          },
        });
        setQuestionText('');
        sendMessage('handleTicket', {
          confirmationMessage: lastMessage?.text,
          text: questionText,
        });
        updateConversation({
          type: 'question',
          text: questionText,
          messageAt: Date.now(),
        });
        return;
      }
      if (temp.length > 0) {
        temp.push({
          type: 'question',
          text: questionText,
          messageAt: Date.now(),
        });
      } else {
        temp.push({
          type: 'question',
          text: questionText,
          messageAt: Date.now(),
        });
      }
      setConversation(temp);
      setQuestionText('');
      const messageData = {
        message: questionText,
        conversationId,
      };
      if (actionTriggerMessage) {
        messageData.originalMessage = actionTriggerMessage;
      }
      if (clearButton) {
        const lastMessage = conversation[conversation?.length - 1];
        if (lastMessage?.type !== 'ticket') {
          updateConversation({
            ...lastMessage,
            type: 'answer',
          });
        }
      }
      sendMessage('chatMessage', messageData);
    }
    dispatch(updateChats([...conversation]));
  };

  const debouncedHandleSendMessage = debounce(handleSendMessage, 1000, {
    leading: true,
    trailing: false,
  });

  const handleUrlsInString = (text) => {
    try {
      text = text.replace(/^### (.*$)/gim, '<h3>$1</h3>');
      text = text.replace(/^## (.*$)/gim, '<h2>$1</h2>');
      text = text.replace(/^# (.*$)/gim, '<h1>$1</h1>');
      // Replace bold and italic
      text = text.replace(/\*\*(.+?)\*\*/gim, '<b>$1</b>');
      // Replace italic (*text*)
      text = text.replace(/\*(.+?)\*/gim, '<i>$1</i>');

      // Replace links
      // eslint-disable-next-line quotes
      text = text.replace(/\[(.+?)\]\((.+?)\)/gim, "<a href='$2'>$1</a>");

      // Replace new lines with <br>
      text = text.replace(/\n/gim, '<br>');

      return text.trim();
    } catch (error) {
      return text;
    }
  };

  const handleActionConfirmation = async (info, confirmationMessage) => {
    try {
      setClearButton(true);
      updateConversation({
        ...info,
        type: 'answer',
      });
      updateConversation({
        text: confirmationMessage,
        type: 'question',
      });
      if (confirmationMessage === 'no') {
        sendMessage('actionConfirmed', {
          conversationId,
          triggerMessage: info?.data?.originalMessage,
          confirmationMessage,
        });
      } else {
        sendMessage('actionConfirmed', {
          conversationId,
          triggerMessage: info?.data?.originalMessage,
          confirmationMessage,
          messageId: info?.messageId,
        });
      }

      dispatch(updateChats([...conversation]));
      setLoading(false);
    } catch (error) {
      console.error(error);
    }
  };

  const handleLimitExceeded = async () => {
    setSignInModal(true);
  };

  const handleFeedBackSubmit = (data) => {
    updateConversation({
      ...data,
      data: {
        ...data?.data,
        feedbackData: {
          isPositive: feedbackData?.isPositive,
          isNegative: !feedbackData?.isPositive,
        },
      },
    });
    sendMessage('feedbackSubmit', data);
    setFeedbackModal(false);
    setFeedbackData({ isPositive: false, comment: '' });
  };

  const handleFeedbackClose = () => {
    setFeedbackModal(false);
    setFeedbackData({ isPositive: false, comment: '' });
  };

  const getActionButtons = (
    info,
    handleActionConfirmation,
    handleCreateTicket
  ) => {
    const { type } = info || {};
    const actionHandlers = {
      ask_action_confirmation: handleActionConfirmation,
      no_answer: handleCreateTicket,
      ticket_suggestion: handleCreateTicket,
    };
    const debouncedHandleAction = debounce(
      (info, action) => {
        handleAction(info, action, true);
      },
      1000,
      { leading: true, trailing: false }
    ); // Adjust debounce delay as needed
    const handleAction = actionHandlers[type];

    if (handleAction) {
      return (
        <div style={{ display: 'flex', gap: '10px', marginTop: '10px' }}>
          <Button onClick={() => debouncedHandleAction(info, 'yes')}>
            Yes
          </Button>
          <Button onClick={() => debouncedHandleAction(info, 'no')}>No</Button>
        </div>
      );
    }
  };

  useEffect(() => {
    if (messageEl?.current) {
      const observer = new MutationObserver(() => {
        messageEl.current.scroll({
          top: messageEl.current.scrollHeight,
          behavior: 'smooth',
        });
      });

      observer.observe(messageEl.current, {
        childList: true,
        subtree: true,
      });

      return () => {
        observer.disconnect();
      };
    }

    // Scroll to the bottom when conversation changes
    if (messageEl?.current) {
      messageEl.current.scrollTop = messageEl.current.scrollHeight;
    }

    if (messageEl2?.current) {
      messageEl2.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [conversation]);

  const getSourceText = (messageData) => {
    const sources = messageData?.data?.answers?.[0]?.sources;
    const messageType = messageData?.type;
    const feedbackData = messageData?.data?.feedbackData;
    const handleFeedback = (isPositive) => {
      setFeedbackModal(true);
      setFeedbackData({
        ...feedbackData,
        ...messageData,
        isPositive,
      });
    };
    if (
      !sources ||
      messageType === 'ticket_suggestion' ||
      messageData?.data?.hideFeedback
    ) {
      return null;
    }
    return (
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          alignSelf: 'center',
          marginTop: '10px',
          gap: '12px',
        }}
      >
        <div>
          {sources?.length > 0 && (
            <div
              style={{
                color: '#402E96',
                fontWeight: 700,
                cursor: 'pointer',
              }}
              onClick={() => {
                setShowSynthesis(true);
                const sourceName = sources.map((source) => source?.name);
                const results = messageData?.data?.answerActivity?.results;
                const filteredSources = results?.filter((el) =>
                  sourceName?.includes(String(el?.metadata?.source?.name))
                );
                setSynthesisData(filteredSources);
              }}
            >
              Sources ({sources?.length})
            </div>
          )}
        </div>
        <div
          span={2}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
          }}
        >
          <button
            style={{
              background: 'none',
              border: 'none',
              padding: 0,
              cursor: 'pointer',
            }}
            onClick={() => handleFeedback(true)}
            disabled={feedbackData}
          >
            <img
              src={feedbackData?.isPositive ? HighlightedLike : Like}
              alt="Button Icon"
              style={{ width: '20px', height: '20px' }}
            />
          </button>
          <button
            style={{
              background: 'none',
              border: 'none',
              padding: 0,
              cursor: 'pointer',
            }}
            onClick={() => handleFeedback(false)}
            disabled={feedbackData}
          >
            <img
              src={feedbackData?.isNegative ? HighlightedUnLike : UnLike}
              alt="Button Icon"
              style={{ width: '20px', height: '20px' }}
            />
          </button>
        </div>
      </div>
    );
  };

  const handleTicketSubmit = async (ticketData, info) => {
    try {
      sendMessage('create_ticket', { ...info, ticketData });
    } catch (error) {
      console.error(error);
    }
  };
  const debouncedHandleTicketSubmit = debounce(handleTicketSubmit, 1000, {
    leading: true,
    trailing: false,
  });

  const handleTicketClose = async () => {
    try {
      setTicketForm({
        isOpen: false,
        isLoading: false,
        data: null,
      });
      updateConversation({
        text: TICKET_CANCEL_TEXT,
        type: 'answer',
        messageId: uuidv4(),
      });
    } catch (error) {
      console.error(error);
    }
  };

  const debouncedHandleTicketClose = debounce(handleTicketClose, 1000, {
    leading: true,
    trailing: false,
  });

  const handleCreateTicket = async (info, action, buttonClick, botId) => {
    setClearButton(true);
    if(buttonClick){
      updateConversation({
        ...info,
        type: 'action',
        data: {
          ...info?.data,
          hideFeedback: true,
        },
      });
    }
    // updateConversation({
    //   text: action,
    //   type: 'question',
    // });

    if (action === 'yes') {
      const messageId = uuidv4();
      setTicketForm({
        isOpen: true,
        isLoading: true,
        data: null,
      });
      const data = await getTicketingFields({
        projectId: info?.data?.ticketInfo?.project?.id,
        requestId: info?.data?.ticketInfo?.requestType?.id,
        serviceDeskId: info?.data?.ticketInfo?.requestType?.serviceDeskId,
        appInstanceId: info?.data?.ticketInfo?.appInstanceId,
        botId: botId || selectedBot?.botId,
      });
      setTicketForm({
        isOpen: true,
        isLoading: false,
        data: {
          ...data,
          description:
            info?.data?.answerActivity?.queryText || info?.data?.messageText,
          projectId: info?.data?.ticketInfo?.project?.id,
          requestId: info?.data?.ticketInfo?.requestType?.id,
          serviceDeskId: info?.data?.ticketInfo?.requestType?.serviceDeskId,
        },
      });
      deleteConversationWithMessageId(messageId);
    }
    if (action === 'no') {
      updateConversation({
        text: TICKET_CANCEL_TEXT,
        type: 'answer',
        messageId: uuidv4(),
      });
    }
  };

  const getTicketCard = (messageData) => {
    const { data } = messageData;

    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        <Text>Ticket has been successfully created.</Text>
        <div style={{ wordBreak: 'break-word' }}>
          <Text strong>
            ⚡{' '}
            <a
              style={{ color: '#402E96' }}
              href={data?.ticketLink}
              target="_blank"
              rel="noreferrer"
            >
              {data?.key}
            </a>
          </Text>
          <Tag style={{ margin: '0 12px' }} color="orange">
            {data?.status}
          </Tag>
          {data?.summary}
        </div>
      </div>
    );
  };

  const renderMessageContent = (messageData) => {
    switch (messageData?.type) {
      case 'ticket':
        return getTicketCard(messageData);
      default:
        return (
          <>
            <span
              className="response_column"
              style={{
                display: 'block',
                textAlign: 'justify',
              }}
              dangerouslySetInnerHTML={{
                __html: handleUrlsInString(messageData.text),
              }}
            />
            {messageData.data && <div>{getSourceText(messageData)}</div>}
          </>
        );
    }
  };

  const MessageCard = ({ messageData }) => (
    <div
      style={{
        background: '#ffffff',
        borderRadius: '8px',
        padding: '16px',
        border: '1px solid #e8e8e8',
      }}
    >
      {renderMessageContent(messageData)}
      <p
        style={{
          margin: 0,
          fontSize: '11px',
          color: '#999',
          marginTop: '10px',
        }}
      >
        {moment(messageData?.messageAt).format('LT')}
      </p>
    </div>
  );

  return (
    <Row style={{ height: '100vh' }}>
      {loading ? (
        <div
          size="large"
          style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            height: '100vh',
            width: '100%',
          }}
        >
          <Spin spinning={true} />
        </div>
      ) : (
        <Col span={24}>
          {isMobile && <TopNavBar />}
          {isMobile && <div style={{ height: '7vh', width: '100%' }} />}
          <div className="playground-chat">
            <div
              style={{
                margin: isMobile ? '0' : '0px 8%',
                height: '100%',
                border: '1px solid #F6F6F6',
              }}
            >
              <div
                className="playground-messages"
                style={{
                  height: isMobile ? '72vh' : '80vh',
                }}
                ref={messageEl}
              >
                {conversation?.length > 0 ? (
                  <>
                    {conversation?.map((messageData, i) => (
                      <div key={i} ref={messageEl2} style={{ borderRadius: 5 }}>
                        <Row key={i} style={{ padding: 5, width: '100%' }}>
                          {messageData?.type === 'question' ? (
                            <div
                              style={{
                                display: 'flex',
                                width: '100%',
                                marginBottom: '16px',
                              }}
                            >
                              <Col span={2}></Col>
                              <Col span={20}>
                                <div
                                  style={{
                                    width: '100%',
                                    background: '#F7F9FC',
                                    borderRadius: '8px',
                                    padding: '12px',
                                    position: 'relative',
                                  }}
                                >
                                  <p
                                    style={{
                                      margin: 0,
                                      fontSize: '14px',
                                      color: '#333',
                                    }}
                                  >
                                    <span
                                      className="response_column"
                                      style={{
                                        display: 'block',
                                        textAlign: 'justify',
                                      }}
                                      dangerouslySetInnerHTML={{
                                        __html: handleUrlsInString(
                                          messageData.text
                                        ),
                                      }}
                                    ></span>
                                  </p>
                                  <p
                                    style={{
                                      margin: 0,
                                      fontSize: '11px',
                                      color: '#999',
                                      marginTop: '10px',
                                    }}
                                  >
                                    {moment(messageData.messageAt).format('LT')}
                                  </p>
                                </div>
                              </Col>
                              <Col
                                span={2}
                                style={{
                                  display: 'flex',
                                  justifyContent: 'end',
                                  flexDirection: 'column',
                                }}
                              >
                                <Avatar
                                  style={{
                                    marginLeft: '8px',
                                    alignSelf: 'center',
                                  }}
                                >
                                  A
                                </Avatar>
                              </Col>
                            </div>
                          ) : (
                            <Row
                              style={{
                                display: 'flex',
                                width: '100%',
                                marginBottom: '16px',
                              }}
                            >
                              <Col
                                span={2}
                                style={{
                                  display: 'flex',
                                  justifyContent: 'end',
                                  flexDirection: 'column',
                                }}
                              >
                                <Avatar
                                  src={EnjoAction}
                                  style={{
                                    marginRight: '8px',
                                    alignSelf: 'center',
                                  }}
                                />
                              </Col>
                              <Col span={20}>
                                <div
                                  style={{
                                    position: 'relative',
                                    width: '100%',
                                  }}
                                >
                                  <MessageCard messageData={messageData} />
                                </div>
                              </Col>
                              <Col span={2}></Col>
                              <Col span={2}></Col>
                              {getActionButtons(
                                messageData,
                                handleActionConfirmation,
                                handleCreateTicket
                              )}
                            </Row>
                          )}
                        </Row>
                        <br />
                      </div>
                    ))}
                  </>
                ) : (
                  <InitialCard
                    setQuestionText={setQuestionText}
                    isMobile={isMobile}
                  />
                )}
              </div>

              <div
                style={
                  isMobile
                    ? {
                        position: 'fixed',
                        bottom: 0,
                        left: 0,
                        right: 0,
                        zIndex: 100,
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        flexDirection: 'column',
                        backgroundColor: 'white',
                        height: '23vh',
                      }
                    : {
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        height: '20vh',
                        flexDirection: 'column',
                        backgroundColor: 'white',
                      }
                }
              >
                <Divider style={{ margin: 0, width: '100%' }} />
                <div
                  style={{
                    padding: '20px 20px 10px 20px',
                    width: '100%',
                  }}
                >
                  <div
                    className={
                      isMobile
                        ? 'message-input-container-mobile'
                        : 'message-input-container'
                    }
                  >
                    {/* Input field */}
                    <Input.TextArea
                      placeholder="Type message"
                      autoSize={
                        isMobile
                          ? { minRows: 1, maxRows: 1 }
                          : { minRows: 2, maxRows: 2 }
                      }
                      className={
                        isMobile ? 'message-input-mobile' : 'message-input'
                      }
                      value={questionText}
                      onChange={(e) => setQuestionText(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' && !e.shiftKey) {
                          e.preventDefault();
                          questionText.trim() &&
                            debouncedHandleSendMessage(questionText);
                        }
                      }}
                    />

                    <div
                      className={
                        isMobile ? 'icons-container-mobile' : 'icons-container'
                      }
                    >
                      <Button
                        icon={<SendOutlined />}
                        type="primary"
                        onClick={() => {
                          return (
                            questionText.trim() &&
                            debouncedHandleSendMessage(questionText)
                          );
                        }}
                        style={{ color: '#fff' }}
                      ></Button>
                    </div>
                  </div>
                </div>
                {isMobile && <BottomNavBar />}
              </div>
            </div>
          </div>
        </Col>
      )}
      <TicketCreateForm
        ticketForm={ticketForm}
        setTicketForm={setTicketForm}
        handleTicketSubmit={debouncedHandleTicketSubmit}
        handleTicketClose={debouncedHandleTicketClose}
      />
      <Modal
        centered
        title={'Provide Feedback'}
        open={feedbackModal}
        closeIcon={<CloseOutlined onClick={() => handleFeedbackClose()} />}
        okText="Save"
        onCancel={() => {
          handleFeedbackClose();
        }}
        onOk={() => {
          handleFeedBackSubmit(feedbackData);
        }}
      >
        <Text strong>Your comments (optional)</Text>
        <Input.TextArea
          value={feedbackData?.comment}
          rows={4}
          placeholder="What did you like about this response? Hoe could it be better?"
          onChange={(e) =>
            setFeedbackData({ ...feedbackData, comment: e.target.value })
          }
        />
      </Modal>
      <Modal
        style={{ padding: '40px' }}
        centered
        closable={false}
        open={signInModal}
        footer={null}
      >
        <Title level={3}>Sign in to continue</Title>

        <Text style={{ marginBottom: '120px', color: '#64778E' }}>
          Access and review your chat history anytime. Your privacy is our
          highest priority. By signing in, you agree to our Privacy Policy and
          Terms of Service.
        </Text>
        <Button
          className="primary-btn onboarding-primary-btn"
          type="primary"
          style={{ width: '100%', borderRadius: '8px', margin: '36px 0px' }}
          onClick={() => {
            window.location.href = 'https://app.enjo.ai/login';
          }}
        >
          Sign in
        </Button>
        <BottomLinksBar />
      </Modal>
      <Drawer
        title={
          <div style={{ display: 'flex', justifyContent: 'space-between' }}>
            <div
              style={{
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                width: '300px',
              }}
            >
              {'Sources'}
            </div>
          </div>
        }
        styles={{
          body: { padding: '20px', overflow: 'hidden' },
          header: {
            height: '56px',
            border: '0px 0px 1px 0px',
            padding: '16px 24px',
          },
        }}
        maskClosable={true}
        width={480}
        getContainer={false}
        className="smooth-drawer"
        open={showSynthesis}
        onClose={() => setShowSynthesis(false)}
      >
        <Synthesis synthesisData={synthesisData} />
      </Drawer>
    </Row>
  );
};

export default Showcase;

const InitialCard = ({ setQuestionText, isMobile }) => {
  const { selectedBot } = useSelector((state) => state.bots);
  const defaultSuggestions = getCenterSuggestions(selectedBot);
  return (
    <div
      style={{
        margin: '0px',
        height: '100%',
        position: 'relative',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <div
        style={{
          height: isMobile ? '50px' : '200px',
          backgroundColor: '#F7F9FC',
          margin: '-24px -24px 30px -24px',
          position: 'relative',
        }}
      >
        <img
          src={EnjoAction}
          width={isMobile ? '40px' : '80px'}
          height={isMobile ? '40px' : '80px'}
          alt="avatar"
          style={{
            position: 'absolute',
            bottom: isMobile ? -20 : -40,
            left: 20,
          }}
        />
      </div>
      <Title level={isMobile ? 4 : 3}>
        Welcome to your Enjo AI Agent playground
      </Title>
      <Text style={{ color: '#7A8B94', fontSize: isMobile ? 12 : 13 }}>
        As an IT Support Manager at Enjo, you&apos;ll explore technical
        solutions, implement support strategies, and address any system-related
        questions. Feel free to try new ideas or reach out for assistance.
      </Text>
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          flex: '1 1 auto',
          gap: '8px',
          width: '100%',
          overflowY: 'auto',
        }}
      >
        <div
          style={{
            display: 'flex',
            flexWrap: 'wrap-reverse',
            justifyContent: 'flex-end',
            alignContent: 'flex-start',
            gap: '8px',
            flex: '1 1 auto',
            margin: '4px',
          }}
        >
          {defaultSuggestions?.map((el) => {
            return (
              <Card
                key={el}
                styles={{
                  body: {
                    padding: isMobile ? '6px' : '8px',
                    fontSize: isMobile ? 12 : 13,
                  },
                }}
                className="suggestion-chat"
                onClick={() => setQuestionText(el)}
              >
                {el}
              </Card>
            );
          })}
        </div>
      </div>
    </div>
  );
};

const Synthesis = ({ synthesisData }) => {
  return (
    <div style={{ height: '100%', overflow: 'scroll' }}>
      {synthesisData?.map((data, i) => (
        <div
          key={i}
          style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}
        >
          <Text style={{ color: '#64778E' }}>Sources {i + 1}:</Text>
          <Text style={{ color: '#402E96', fontWeight: 700 }}>
            {data.metadata.source?.name}
          </Text>
          <Text>{data.metadata?.pageContent}</Text>
          <br />
        </div>
      ))}
      <br />
    </div>
  );
};
